type TagNames = keyof HTMLElementTagNameMap | keyof SVGElementTagNameMap;
type IgnoredSelectors = TagNames | 'svg *';

type DomElementCount =
    | {
          measurementSuccessful: true;
          totalElements: number;
          mdsElements: number;
          adoptionPercentage: number;
      }
    | {
          measurementSuccessful: false;
          error: string;
      };

const ignoredSelectors: Array<IgnoredSelectors> = [
    'script',
    'style',
    'base',
    'noscript',
    'iframe',
    'br',
    'title',
    'svg',
    'svg *',
    'path',
    'img',
    'form',
];

const ignoredQuery = `:not([data-test-textkey], [data-localized-entity], :empty):not(${ignoredSelectors.join(',')})`;

/**
 * Counts DOM elements and calculates adoption metrics for design system components
 * @param options - Configuration options
 * @returns Object containing count metrics or error information
 */
export function countDomElements({
    exclude,
}: {
    /**
     * app-specific overrides (e.g. common layouts)
     * comma-separated list of selectors to exclude from the count
     */
    exclude?: string;
} = {}): DomElementCount {
    try {
        const finalIgnoredQuery = exclude ? `${ignoredQuery}:not(${exclude})` : ignoredQuery;
        const allElements = document.body.querySelectorAll(finalIgnoredQuery);
        const dsElements = document.body.querySelectorAll(`[data-mds-element]${finalIgnoredQuery}`);

        return {
            measurementSuccessful: true,
            totalElements: allElements.length,
            mdsElements: dsElements.length,
            adoptionPercentage: Number(((dsElements.length / allElements.length) * 100).toFixed(2)),
        };
    } catch (error) {
        // old browsers might fail on querySelectorAll(:not), only log in development mode
        if (process.env.NODE_ENV === 'development') {
            // eslint-disable-next-line no-console
            console.warn('Design system adoption measurement: Failed to query DOM elements with complex selectors.', {
                error,
                browser: navigator.userAgent,
                selector: exclude ? `${ignoredQuery}:not(${exclude})` : ignoredQuery,
            });
        }

        return {
            measurementSuccessful: false,
            error: 'Failed to query DOM elements',
        };
    }
}

export const COUNT_DOM_ELEMENTS_INTERVAL = 10000;
